import React, { useEffect, useState, useCallback } from "react";
import { FaChevronLeft, FaChevronRight, FaSearch, FaUser, FaClipboardList, FaFilePdf } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import jsPDF from "jspdf";

type View = "patients" | "studies" | "detail";

interface Patient {
  patientname: string;
  total_checks: string;
  last_check: string;
  live_view_count: string;
  // Most recent system this patient was checked on (a patient can move
  // across systems over time; we show the latest).
  systemname: string | null;
}

interface StudyLog {
  id: number;
  timestamp: string;
  patientname: string;
  note: string | null;
  username: string;
  systemname: string;
}

interface LiveViewSession {
  id: number;
  username: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
}

interface Study {
  studyIndex: number;
  systemname: string;
  start: string;
  end: string;
  effective_start: number;
  effective_end: number;
  check_count: number;
  logs: StudyLog[];
  live_view_sessions: LiveViewSession[];
}

const fmt = (ts: string | number) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(new Date(ts));

const fmtDate = (ts: string | number) =>
  new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(new Date(ts));

const duration = (start: string | number, end: string | number) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const th: React.CSSProperties = {
  padding: "12px 16px", fontWeight: 600, fontSize: "12px",
  textTransform: "uppercase", letterSpacing: "0.05em",
  color: "#64748b", background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "14px 16px", fontSize: "14px", color: "#1e293b",
  borderBottom: "1px solid #f1f5f9", verticalAlign: "middle",
};

const CheckLog: React.FC = () => {
  const [view, setView] = useState<View>("patients");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [studies, setStudies] = useState<Study[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<string>("");
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [checkLogs, setCheckLogs] = useState<StudyLog[]>([]);
  const [searchDate, setSearchDate] = useState("");

  // ── Patients view ──────────────────────────────────────────────
  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/v1/checklogs/patients", { credentials: "include" });
      const data = await res.json();
      setPatients(data.patients);
    } finally { setLoading(false); }
  }, []);

  // ── All logs view (search) ─────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(currentPage) });
      if (search) params.set("search", search);
      if (searchDate) params.set("date", searchDate);
      const res = await fetch(`/api/v1/checklogs?${params}`, { credentials: "include" });
      const data = await res.json();
      setCheckLogs(data.logs);
      setTotalPages(data.totalPages);
    } finally { setLoading(false); }
  }, [currentPage, search, searchDate]);

  // ── Patient studies ────────────────────────────────────────────
  const openPatient = async (name: string) => {
    setSelectedPatient(name);
    setView("studies");
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/checklogs/patients/${encodeURIComponent(name)}`, { credentials: "include" });
      const data = await res.json();
      setStudies(data.studies);
    } finally { setLoading(false); }
  };

  const openStudy = (study: Study) => {
    setSelectedStudy(study);
    setView("detail");
  };

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleSearchChange = (s: string, d: string) => {
    setSearch(s); setSearchDate(d); setCurrentPage(1);
  };

  const generatePDF = (study: Study) => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 16;
    const contentW = pageW - margin * 2;
    let y = margin;

    const checkY = (needed = 10) => {
      if (y + needed > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const line = (text: string, size = 11, bold = false, color: [number, number, number] = [30, 41, 59]) => {
      checkY(size + 4);
      doc.setFontSize(size);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, contentW);
      doc.text(lines, margin, y);
      y += (lines.length * (size * 0.4)) + 4;
    };

    const divider = () => {
      checkY(6);
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, pageW - margin, y);
      y += 6;
    };

    // Header
    doc.setFillColor(33, 150, 243);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Patient Study Report", margin, 18);
    y = 36;

    // Patient & study info
    line(`Patient: ${selectedPatient}`, 13, true);
    line(`System: ${study.systemname}`, 11, false, [100, 116, 139]);
    line(`Study Period: ${fmt(study.effective_start ?? study.start)} — ${fmt(study.effective_end ?? study.end)}`, 11, false, [100, 116, 139]);
    line(`Duration: ${duration(study.effective_start ?? study.start, study.effective_end ?? study.end)}   •   Total Checks: ${study.check_count}`, 11, false, [100, 116, 139]);
    line(`Report Generated: ${fmt(new Date().toISOString())}`, 10, false, [148, 163, 184]);
    y += 4;
    divider();

    // Timeline
    line("Study Timeline", 13, true);
    y += 2;

    const allEvents: { ts: string; label: string; sub?: string }[] = [
      ...study.logs.map(l => ({
        ts: l.timestamp,
        label: `Check — ${l.username}`,
        sub: l.note || "No notes",
      })),
...(study.live_view_sessions || []).map(l => {
        const dur = l.duration_seconds
          ? `${Math.floor(l.duration_seconds / 60)}m ${l.duration_seconds % 60}s`
          : l.ended_at ? "< 1s" : "interrupted";
        return { ts: l.started_at, label: `Live View — ${l.username} (${dur})` };
      }),
    ].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

    for (const event of allEvents) {
      checkY(18);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(fmt(event.ts), margin, y);

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      const labelLines = doc.splitTextToSize(event.label, contentW - 52);
      doc.text(labelLines, margin + 52, y);
      y += labelLines.length * 5;

      if (event.sub) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100, 116, 139);
        const subLines = doc.splitTextToSize(event.sub, contentW - 52);
        doc.text(subLines, margin + 52, y);
        y += subLines.length * 4;
      }
      y += 4;
    }

    doc.save(`${selectedPatient.replace(/\s+/g, "_")}_Study_${fmtDate(study.start).replace(/\s/g, "_")}.pdf`);
  };

  const filteredPatients = patients.filter(p =>
    p.patientname?.toLowerCase().includes(search.toLowerCase())
  );

  const pill = (text: string, color = "#1976D2", bg = "#eff6ff") => (
    <span style={{ background: bg, color, padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600 }}>
      {text}
    </span>
  );

  const spinner = (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
      <div style={{ textAlign: "center" }}>
        <ImSpinner2 style={{ fontSize: "28px", color: "#2196F3", animation: "spin 1s linear infinite" }} />
        <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>Loading...</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {view !== "patients" && (
              <button onClick={() => { setView(view === "detail" ? "studies" : "patients"); setSelectedStudy(null); }}
                style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                <FaChevronLeft /> Back
              </button>
            )}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#94a3b8", marginBottom: "4px" }}>
                <span style={{ cursor: view !== "patients" ? "pointer" : "default", color: view !== "patients" ? "#2196F3" : "#94a3b8" }}
                  onClick={() => { setView("patients"); setSelectedStudy(null); }}>
                  Patients
                </span>
                {view !== "patients" && <><FaChevronRight style={{ fontSize: "10px" }} /><span style={{ color: view === "detail" ? "#2196F3" : "#1e293b" }}
                  onClick={() => view === "detail" && setView("studies")}>{selectedPatient}</span></>}
                {view === "detail" && selectedStudy && <><FaChevronRight style={{ fontSize: "10px" }} /><span style={{ color: "#1e293b" }}>Study {selectedStudy.check_count} checks</span></>}
              </div>
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1e293b" }}>
                {view === "patients" ? "Check Logs" : view === "studies" ? selectedPatient : `Study — ${fmtDate(selectedStudy!.start)}`}
              </h2>
            </div>
          </div>
        </div>

        {/* Search (patients view only) */}
        {view === "patients" && (
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: 1, minWidth: "200px" }}>
              <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "13px" }} />
              <input type="text" placeholder="Search by patient name..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", outline: "none", color: "#1e293b" }} />
            </div>
          </div>
        )}

        {loading ? spinner : (
          <>
            {/* ── Patients Table ── */}
            {view === "patients" && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Patient</th>
                      <th style={th}>System</th>
                      <th style={th}>Total Checks</th>
                      <th style={th}>Live Views</th>
                      <th style={th}>Last Check</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.length === 0 ? (
                      <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "#94a3b8", padding: "48px" }}>No patients found</td></tr>
                    ) : filteredPatients.map((p) => (
                      <tr key={p.patientname} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                        onClick={() => openPatient(p.patientname)}>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ background: "#eff6ff", borderRadius: "50%", width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <FaUser style={{ color: "#1976D2", fontSize: "14px" }} />
                            </div>
                            <span style={{ fontWeight: 500 }}>{p.patientname}</span>
                          </div>
                        </td>
                        <td style={{ ...td, color: "#64748b", fontSize: "13px" }}>{p.systemname || "—"}</td>
                        <td style={td}>{pill(p.total_checks + " checks", "#059669", "#f0fdf4")}</td>
                        <td style={td}>{Number(p.live_view_count) > 0 ? pill(p.live_view_count + " live views", "#6d28d9", "#faf5ff") : <span style={{ color: "#94a3b8", fontSize: "13px" }}>—</span>}</td>
                        <td style={{ ...td, color: "#64748b", fontSize: "13px" }}>{fmt(p.last_check)}</td>
                        <td style={{ ...td, textAlign: "right" }}>
                          <span style={{ color: "#2196F3", fontSize: "13px", fontWeight: 500 }}>View Studies →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Studies List ── */}
            {view === "studies" && (
              <div style={{ padding: "16px 24px", display: "grid", gap: "12px" }}>
                {studies.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#94a3b8", padding: "48px 0" }}>No studies found</p>
                ) : studies.map((study) => (
                  <div key={study.studyIndex} onClick={() => openStudy(study)}
                    style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", cursor: "pointer", transition: "all 0.15s" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "#2196F3"; (e.currentTarget as HTMLDivElement).style.boxShadow = "0 2px 12px rgba(33,150,243,0.1)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLDivElement).style.boxShadow = "none"; }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <FaClipboardList style={{ color: "#2196F3" }} />
                          <span style={{ fontWeight: 600, fontSize: "15px", color: "#1e293b" }}>
                            Study {studies.length - study.studyIndex}
                          </span>
                          {pill(study.check_count + " checks", "#059669", "#f0fdf4")}
                          {(study.live_view_sessions?.length || 0) > 0 && pill(study.live_view_sessions.length + " live views", "#6d28d9", "#faf5ff")}
                        </div>
                        <div style={{ fontSize: "13px", color: "#64748b", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                          <span><strong>System:</strong> {study.systemname}</span>
                          <span><strong>Started:</strong> {fmt(study.effective_start ?? study.start)}</span>
                          <span><strong>Ended:</strong> {fmt(study.effective_end ?? study.end)}</span>
                          <span><strong>Duration:</strong> {duration(study.effective_start ?? study.start, study.effective_end ?? study.end)}</span>
                        </div>
                      </div>
                      <FaChevronRight style={{ color: "#94a3b8", marginTop: "4px" }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Study Detail ── */}
            {view === "detail" && selectedStudy && (() => {
              type TimelineEvent =
                | { kind: "check"; ts: string; data: StudyLog }
                | { kind: "live"; ts: string; data: LiveViewSession };

              const timeline: TimelineEvent[] = [
                ...selectedStudy.logs.map(l => ({ kind: "check" as const, ts: l.timestamp, data: l })),
                ...(selectedStudy.live_view_sessions || []).map(l => ({ kind: "live" as const, ts: l.started_at, data: l })),
              ].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

              return (
                <>
                  <div style={{ padding: "12px 24px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: "24px", fontSize: "13px", color: "#64748b" }}>
                      <span><strong>System:</strong> {selectedStudy.systemname}</span>
                      <span><strong>Duration:</strong> {duration(selectedStudy.effective_start ?? selectedStudy.start, selectedStudy.effective_end ?? selectedStudy.end)}</span>
                      <span><strong>Checks:</strong> {selectedStudy.check_count}</span>
                    </div>
                    <button
                      onClick={() => generatePDF(selectedStudy)}
                      style={{ display: "flex", alignItems: "center", gap: "7px", background: "linear-gradient(135deg, #dc2626, #b91c1c)", color: "white", border: "none", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
                    >
                      <FaFilePdf /> Generate PDF Report
                    </button>
                  </div>
                  <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {timeline.map((event, i) => {
                      if (event.kind === "check") {
                        const log = event.data as StudyLog;
                        return (
                          <div key={`check-${log.id}`} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                            <div style={{ flexShrink: 0, marginTop: "2px" }}>
                              <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#10b981", border: "2px solid white", boxShadow: "0 0 0 2px #10b981" }} />
                            </div>
                            <div style={{ flex: 1, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "12px 16px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                <span style={{ fontWeight: 600, fontSize: "13px", color: "#059669" }}>Check — {log.username}</span>
                                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{fmt(log.timestamp)}</span>
                              </div>
                              <span style={{ fontSize: "13px", color: log.note ? "#374151" : "#94a3b8", fontStyle: log.note ? "normal" : "italic" }}>
                                {log.note || "No notes"}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      if (event.kind === "live") {
                        const session = event.data as LiveViewSession;
                        const dur = session.duration_seconds
                          ? `${Math.floor(session.duration_seconds / 60)}m ${session.duration_seconds % 60}s`
                          : session.ended_at ? "< 1s" : "interrupted";
                        return (
                          <div key={`live-${session.id}`} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                            <div style={{ flexShrink: 0, marginTop: "2px" }}>
                              <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#7c3aed", border: "2px solid white", boxShadow: "0 0 0 2px #7c3aed" }} />
                            </div>
                            <div style={{ flex: 1, background: "#faf5ff", border: "1px solid #ddd6fe", borderRadius: "10px", padding: "12px 16px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontWeight: 600, fontSize: "13px", color: "#6d28d9" }}>Live View — {session.username} ({dur})</span>
                                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{fmt(session.started_at)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </>
              );
            })()}
          </>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CheckLog;
